import { useState } from 'react'
import { dashboardModule, dashboardWidget, resolveWidgetView, widgetViews, type WidgetView } from '@/lib/dashboardWidgets'
import type { WidgetPlacement } from '@/lib/dashboardLayouts'
import { BUILT_WIDGETS } from './builtWidgets'
import { useDashboardPeriod } from './DashboardPeriod'
import { ViewSwitcher } from './views'
import { WidgetCard, WidgetUnavailable } from './WidgetCard'

/**
 * One placed widget: its card, its body drawn in the current view, and the
 * switcher for the other ways its data can be shown.
 *
 * Outside edit mode a click shows a view for this visit only (`shown`), and a
 * double-click saves it as the widget's default through `onMakeDefault`. In
 * edit mode (`onViewChange`) a click goes straight into the draft.
 */
export function DashboardWidget({
  placement,
  onViewChange,
  onMakeDefault,
}: {
  placement: WidgetPlacement
  /** Edit mode: a click sets the view in the draft. */
  onViewChange?: (view: WidgetView) => void
  /** View mode: a double-click saves the view as the widget's default. */
  onMakeDefault?: (view: WidgetView) => void
}) {
  const { time } = useDashboardPeriod()
  const [shown, setShown] = useState<WidgetView | null>(null)
  const def = dashboardWidget(placement.id)
  const module = def ? dashboardModule(def.module) : undefined
  if (!def || !module) return null
  const Body = def.id in BUILT_WIDGETS ? BUILT_WIDGETS[def.id] : undefined
  const defaultView = resolveWidgetView(def.id, placement.view)
  const view = onViewChange ? defaultView : resolveWidgetView(def.id, shown ?? defaultView)
  const interactive = !!Body && (!!onViewChange || !!onMakeDefault)
  return (
    <WidgetCard
      title={def.title}
      description={def.description}
      moduleId={module.id}
      moduleLabel={module.label}
      moduleTo={module.to}
      actions={
        interactive ? (
          <ViewSwitcher
            views={widgetViews(def.id)}
            value={view}
            defaultView={defaultView}
            title={def.title}
            onChange={onViewChange ?? setShown}
            onMakeDefault={
              onViewChange
                ? undefined
                : v => {
                    setShown(null)
                    onMakeDefault?.(v)
                  }
            }
          />
        ) : undefined
      }
    >
      {Body ? <Body time={time} size={placement.size} view={view} /> : <WidgetUnavailable />}
    </WidgetCard>
  )
}
